/** GPU Reduce Noise — fragment-shader port of
 *  `lib/filters/reduce-noise.ts`. Standard bilateral filter.
 *
 *  Per output pixel: sample the (2r+1) × (2r+1) neighborhood;
 *  weight each tap by spatial-Gaussian * range-Gaussian; output the
 *  weighted average mixed with the source via `strength/100`.
 *
 *  Edge-preserving — pixels on the far side of a bright edge
 *  contribute near-zero range weight so edges stay sharp while
 *  smooth regions get smoothed.
 *
 *  Math runs in 0..255 domain to match the JS path's
 *  Uint8ClampedArray byte arithmetic precisely (range Gaussian's
 *  dot(d, d) is on 0..255 differences, so rangeSigma is in the
 *  same units; running in normalized 0..1 would require dividing
 *  the sigma by 255 and risks drift in the exponent).
 *
 *  Constant-bounded loop with runtime cutoff: MAX_R = 5 (matches
 *  the UI cap). Programmatic callers with radius > 5 fall back to
 *  CPU via the `applyReduceNoiseOnGpu` guard. The kernel is
 *  radially symmetric so the runtime's bottom-up `px.y` convention
 *  doesn't matter — the set of sampled pixels is identical to
 *  CPU's. No Y-flip required. */

import type { ReduceNoiseValue } from '../../filters/reduce-noise';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const MAX_RADIUS = 5;

const REDUCE_NOISE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
uniform float u_twoSpatialSq;  // 2 * r^2 (spatial sigma squared)
uniform float u_twoRangeSq;    // 2 * rangeSigma^2 (in 0..255 domain)
uniform float u_mix;           // strength / 100
uniform float u_inv;           // 1 - mix
out vec4 outColor;

void main() {
  vec2 px = v_uv * u_dims - 0.5;
  vec4 srcSample = texture(u_source, v_uv);
  vec3 cRgb = srcSample.rgb * 255.0;

  vec3 sum = vec3(0.0);
  float sumW = 0.0;

  for (int dy = -${MAX_RADIUS}; dy <= ${MAX_RADIUS}; dy++) {
    if (dy < -u_radius || dy > u_radius) continue;
    for (int dx = -${MAX_RADIUS}; dx <= ${MAX_RADIUS}; dx++) {
      if (dx < -u_radius || dx > u_radius) continue;
      float fdx = float(dx);
      float fdy = float(dy);
      // Spatial weight: Gaussian on pixel distance.
      float spatial = exp(-(fdx * fdx + fdy * fdy) / u_twoSpatialSq);
      vec2 sxy = clamp(px + vec2(fdx, fdy), vec2(0.0), u_dims - 1.0);
      vec3 nRgb = texture(u_source, (sxy + 0.5) / u_dims).rgb * 255.0;
      // Range weight: Gaussian on color distance (0..255 per channel).
      vec3 d = cRgb - nRgb;
      float rangeW = exp(-dot(d, d) / u_twoRangeSq);
      float totalW = spatial * rangeW;
      sum += nRgb * totalW;
      sumW += totalW;
    }
  }

  // sumW is always positive (center tap contributes spatial=1,
  // range=1) so no divide-by-zero guard needed — same as JS.
  vec3 bilateral = sum / sumW;
  vec3 result = (cRgb * u_inv + bilateral * u_mix) / 255.0;
  outColor = vec4(clamp(result, 0.0, 1.0), srcSample.a);
}
`;

export function applyReduceNoiseOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ReduceNoiseValue,
): boolean {
  // Same clamp the CPU path uses — UI caps radius at 5, and exceeding
  // values are silently truncated by both paths to keep byte parity.
  const radius = Math.max(0, Math.min(MAX_RADIUS, Math.floor(value.radius)));
  const strength = Math.max(0, Math.min(100, value.strength));
  if (radius === 0 || strength === 0) return applyIdentityCopy(source, target);
  const rangeSigma = Math.max(1, value.rangeSigma);
  const spatialSigma = radius;
  const twoSpatialSq = 2 * spatialSigma * spatialSigma;
  const twoRangeSq = 2 * rangeSigma * rangeSigma;
  const mix = strength / 100;
  return applyShader({
    source,
    target,
    fragShaderSrc: REDUCE_NOISE_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_radius: { type: 'int', value: radius },
      u_twoSpatialSq: { type: 'float', value: twoSpatialSq },
      u_twoRangeSq: { type: 'float', value: twoRangeSq },
      u_mix: { type: 'float', value: mix },
      u_inv: { type: 'float', value: 1 - mix },
    },
  });
}
